import React, { useState, useEffect } from "react";
import {
  challengeService,
  ChallengeCreate,
} from "../services/challengeService";
import { stockService, StockUniverse } from "../services/stockService";
import { cryptoService } from "../services/cryptoService";
import { FiHelpCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import { CryptoUniverse } from "../services/cryptoService";
import { isStockTradingMode } from "../config";

interface ChallengeCreateFormProps {
  onBackToChallengeList: () => void;
  onChallengeCreated: () => void;
}

export default function ChallengeCreateForm({
  onBackToChallengeList,
  onChallengeCreated,
}: ChallengeCreateFormProps) {
  const [formData, setFormData] = useState<ChallengeCreate>({
    name: "",
    description: "",
    type: "Weekly",
    entry_fee: 0,
    prize_pool: 0,
    max_participants: 100,
    min_participants: 2,
    gameplay_type: "DYNAMIC",
    allow_overflow_entries: false,
    start_date: "",
    end_date: "",
    stock_universe_ids: [],
    trading_hours_settings: {
      enabled: isStockTradingMode ? true : false,
      fixed_window_start_time: null,
      fixed_window_end_time: null,
      cancel_unexecuted_fixed_limits: true,
      restrict_to_market_hours: true,
    },
    virtual_capital: 1000000,
    brokerage_fee: 0.05,
    challenge_type: "Flexible",
    guaranteed_prize_pool: 0,
    experience_level: "",
    risk_appetite: "",
    allowed_order_types: {
      market_order: true,
      limit_orders: false,
      stop_loss_orders: false,
    },
    trading_restrictions: {
      allow_short_selling: false,
      intraday_only: false,
      max_open_positions: 10,
      max_capital_per_stock: 25,
    },
    prize_distribution: [
      { rank: 1, percentage: 35 },
      { rank: 2, percentage: 30 },
      { rank: 3, percentage: 20 },
      { rank: 4, percentage: 10 },
      { rank: 5, percentage: 5 },
    ],
    rules: "",
    category: "Equity",
    duration_tag: "Weekly",
    platform_stake: 15,
    info_box_text: "",
    first_prize_icon: "🏆",
    win_percentage_icon: "📈",
    max_entries_icon: "Ⓜ️",
  });
  useEffect(() => {
    if (formData.experience_level === "beginner") {
      const restrictions = formData.trading_restrictions ?? {
        allow_short_selling: false,
        intraday_only: false,
        max_open_positions: 10,
        max_capital_per_stock: 25,
      };

      if (restrictions.max_open_positions !== 5) {
        setFormData((prev) => ({
          ...prev,
          trading_restrictions: {
            ...restrictions,
            max_open_positions: 5,
          },
        }));
      }
    }
  }, [formData.experience_level]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stockUniverses, setStockUniverses] = useState<StockUniverse[]>([]);
  const [cryptoUniverses, setCryptoUniverses] = useState<CryptoUniverse[]>([]);
  const [loadingUniverses, setLoadingUniverses] = useState(true);

  // Fetch stock universes on component mount

  useEffect(() => {
    const fetchUniverses = async () => {
      setLoadingUniverses(true);
      try {
        if (isStockTradingMode) {
          const response = await stockService.getActiveStockUniverses();
          setStockUniverses(response.universes || []);
        } else {
          const response = await cryptoService.getActiveCryptoUniverses();
          setCryptoUniverses(response.universes || []);
        }
      } catch (error) {
        console.error("Error fetching universes:", error);
      } finally {
        setLoadingUniverses(false);
      }
    };

    fetchUniverses();
  }, [isStockTradingMode]);
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleOrderTypeChange = (orderType: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      allowed_order_types: {
        ...prev.allowed_order_types!,
        [orderType]: checked,
      },
    }));
  };

  const handleTradingRestrictionChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      trading_restrictions: {
        ...prev.trading_restrictions!,
        [field]: value,
      },
    }));
  };

  const handleExperienceLevel = (level: string) => {
    setFormData((prev) => ({
      ...prev,
      experience_level: level,
    }));
  };
  const handleRiskAppetite = (level: string) => {
    setFormData((prev) => ({
      ...prev,
      risk_appetite: level,
    }));
  };

  const handlePrizeDistributionChange = (
    index: number,
    field: "rank" | "percentage",
    value: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      prize_distribution:
        prev.prize_distribution?.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ) || [],
    }));
  };

  const addPrizeDistribution = () => {
    const newRank = (formData.prize_distribution?.length || 0) + 1;
    setFormData((prev) => ({
      ...prev,
      prize_distribution: [
        ...(prev.prize_distribution || []),
        { rank: newRank, percentage: 0 },
      ],
    }));
  };

  const removePrizeDistribution = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      prize_distribution:
        prev.prize_distribution
          ?.filter((_, i) => i !== index)
          .map((item, i) => ({
            ...item,
            rank: i + 1,
          })) || [],
    }));
  };

  const getTotalPercentage = () => {
    return (
      formData.prize_distribution?.reduce(
        (sum, item) => sum + item.percentage,
        0
      ) || 0
    );
  };

  const handleTradingHoursChange = (field: string, value: any) => {
    setFormData({
      ...formData,
      trading_hours_settings: {
        enabled: isStockTradingMode
          ? formData.trading_hours_settings?.enabled ?? true
          : false,
        fixed_window_start_time:
          formData.trading_hours_settings?.fixed_window_start_time ?? null,
        fixed_window_end_time:
          formData.trading_hours_settings?.fixed_window_end_time ?? null,
        cancel_unexecuted_fixed_limits:
          formData.trading_hours_settings?.cancel_unexecuted_fixed_limits ??
          true,
        restrict_to_market_hours:
          formData.trading_hours_settings?.restrict_to_market_hours ?? true,
        [field]: value,
      },
    });
  };
  console.log(formData, "============formData===========");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Prize distribution validation
    if (getTotalPercentage() !== 100) {
      toast.error(
        "Prize Distribution Error: The total percentage of all prize tiers must equal 100%. Please adjust the values before proceeding."
      );
      setLoading(false);
      return;
    }
    if (
      formData.entry_fee > 0 &&
      (!formData.prize_pool || formData.prize_pool <= 0)
    ) {
      toast.error(
        "Prize Pool is required and must be greater than 0 when Entry Fee is greater than 0."
      );
      setLoading(false);
      return;
    }
    if (formData.entry_fee > 0 && formData.prize_pool <= formData.entry_fee) {
      toast.error("Prize Pool must be greater than Entry Fee.");
      setLoading(false);
      return;
    }
    const now = new Date();

    if (!formData.start_date) {
      toast.error("Start date is required");
      setLoading(false);
      return;
    }

    if (!formData.end_date) {
      toast.error("End date is required");
      setLoading(false);
      return;
    }

    const start = new Date(formData.start_date);
    if (start < now) {
      toast.error("Start date/time must be in the future");
      setLoading(false);
      return;
    }

    const end = new Date(formData.end_date);
    if (end <= start) {
      toast.error("End date/time must be after start date/time");
      setLoading(false);
      return;
    }
    if (
      !formData.stock_universe_ids ||
      formData.stock_universe_ids.length === 0
    ) {
      toast.error("Please select at least one Stock Universe.");
      setLoading(false);
      return;
    }
    try {
      await challengeService.createChallenge(formData);
      onChallengeCreated();
    } catch (err: any) {
      console.error("Error creating challenge:", err);
      setError(err.message || "Failed to create challenge");
    } finally {
      setLoading(false);
    }
  };

  const HelpIcon = ({ text }: { text: string }) => (
    <div className="relative group">
      <FiHelpCircle className="inline ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
        {text}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-gray-100 font-inter p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBackToChallengeList}
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              ← Back to Challenge List
            </button>
            <h1 className="text-3xl font-bold text-blue-800">
              Create New Challenge
            </h1>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600">{error}</p>
              </div>
            )}

            {/* Section 1: Basic Details & Categorization */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 1: Basic Details & Categorization
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Challenge Name *{" "}
                    <HelpIcon text="Enter a descriptive name for the challenge (e.g., 'Weekly Winner - June W3')" />
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Weekly Winner - June W3"
                  />
                </div>

                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Category{" "}
                    <HelpIcon text="Select the trading category for this challenge" />
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Equity">Equity</option>
                    <option value="F&O">F&O</option>
                    <option value="Commodities">Commodities</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="duration_tag"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Duration Tag{" "}
                    <HelpIcon text="Select the duration type for this challenge" />
                  </label>
                  <select
                    id="duration_tag"
                    name="duration_tag"
                    value={formData.duration_tag}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Daily">Daily (9:15 AM - 3:30 PM)</option>
                    <option value="Weekly">Weekly (5 trading days)</option>
                    <option value="Monthly">
                      Monthly (full calendar month)
                    </option>
                    <option value="Mega">Mega (special event tag)</option>
                    <option value="Practice">Practice (free-to-enter)</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Challenge Type *{" "}
                    <HelpIcon text="Select the type of challenge" />
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Guaranteed">Guaranteed</option>
                    <option value="Satellite">Satellite</option>
                    <option value="Special">Special</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Description{" "}
                  <HelpIcon text="Brief marketing description shown to users in the app" />
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter challenge description"
                />
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="start_date"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Start Date & Time *{" "}
                    <HelpIcon text="Set the exact moment the challenge becomes live for trading" />
                  </label>
                  <input
                    type="datetime-local"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="end_date"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    End Date & Time *{" "}
                    <HelpIcon text="Set the exact moment the challenge ends and trading stops" />
                  </label>
                  <input
                    type="datetime-local"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Rules & Parameters */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 2: Rules & Parameters
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isStockTradingMode
                      ? "Stock Universe *"
                      : "Crypto Universe *"}{" "}
                    <HelpIcon
                      text={`Define which ${
                        isStockTradingMode ? "stocks" : "cryptos"
                      } are tradable within this challenge`}
                    />
                  </label>

                  {loadingUniverses ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      Loading {isStockTradingMode ? "stock" : "crypto"}{" "}
                      universes...
                    </div>
                  ) : (
                    <div className="w-full border border-gray-300 rounded-md p-3 max-h-32 overflow-y-auto">
                      {(isStockTradingMode ? stockUniverses : cryptoUniverses)
                        .length === 0 ? (
                        <p className="text-gray-500 text-sm">
                          No {isStockTradingMode ? "stock" : "crypto"} universes
                          available. Please create some first.
                        </p>
                      ) : (
                        (isStockTradingMode
                          ? stockUniverses
                          : cryptoUniverses
                        ).map((universe) => (
                          <label
                            key={universe.id}
                            className="flex items-center space-x-2 py-1"
                          >
                            <input
                              type="checkbox"
                              value={universe.id}
                              checked={formData.stock_universe_ids?.includes(
                                universe.id
                              )}
                              onChange={(e) => {
                                const selectedIds =
                                  formData.stock_universe_ids || [];

                                if (e.target.checked) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    stock_universe_ids: [
                                      ...selectedIds,
                                      universe.id,
                                    ],
                                  }));
                                } else {
                                  setFormData((prev) => ({
                                    ...prev,
                                    stock_universe_ids: selectedIds.filter(
                                      (id) => id !== universe.id
                                    ),
                                  }));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />

                            <span className="text-sm text-gray-900">
                              {universe.name}
                            </span>
                            {universe.description && (
                              <span className="text-xs text-gray-500">
                                ({universe.description})
                              </span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="virtual_capital"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Virtual Capital ($){" "}
                    <HelpIcon text="Starting virtual money for participants (e.g., 10,00,000)" />
                  </label>
                  <input
                    type="number"
                    id="virtual_capital"
                    name="virtual_capital"
                    value={formData.virtual_capital}
                    onChange={handleInputChange}
                    min="100000"
                    step="100000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="10,00,000"
                  />
                </div>

                <div>
                  <label
                    htmlFor="brokerage_fee"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Brokerage Fee (%){" "}
                    <HelpIcon text="Virtual brokerage percentage charged on every trade (e.g., 0.05). Can be set to 0 for beginner challenges" />
                  </label>
                  <input
                    type="number"
                    id="brokerage_fee"
                    name="brokerage_fee"
                    value={formData.brokerage_fee}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.05"
                  />
                </div>

                <div>
                  <label
                    htmlFor="max_participants"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Total Spots{" "}
                    <HelpIcon text="Maximum number of participants allowed in the challenge" />
                  </label>
                  <input
                    type="number"
                    id="max_participants"
                    name="max_participants"
                    value={formData.max_participants}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="100"
                  />
                </div>

                <div>
                  <label
                    htmlFor="min_participants"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Min Participants *{" "}
                    <HelpIcon text="Minimum participants required for challenge to start" />
                  </label>
                  <input
                    type="number"
                    id="min_participants"
                    name="min_participants"
                    value={formData.min_participants}
                    onChange={handleInputChange}
                    min="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="2"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Minimum participants required for challenge to start
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="gameplay_type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Gameplay Type *{" "}
                    <HelpIcon text="Dynamic: Users can trade during the challenge. Fixed: Users set portfolio at start, no trading during challenge." />
                  </label>
                  <select
                    id="gameplay_type"
                    name="gameplay_type"
                    value={formData.gameplay_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="DYNAMIC">Dynamic Portfolio</option>
                    <option value="FIXED">Fixed Portfolio</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    {formData.gameplay_type === "DYNAMIC"
                      ? "Users can actively trade during the challenge."
                      : "Users set portfolio at start, no trading during challenge."}
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3: Financials & Prize Model */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 3: Financials & Prize Model
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label
                    htmlFor="challenge_type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Challenge Type *{" "}
                    <HelpIcon text="Critical dropdown: 'Flexible' (prize pool based on entry fees minus rake) or 'Guaranteed' (promises a minimum prize pool)" />
                  </label>
                  <select
                    id="challenge_type"
                    name="challenge_type"
                    value={formData.challenge_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Flexible">Flexible</option>
                    <option value="Guaranteed">Guaranteed</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="entry_fee"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Entry Fee ($){" "}
                    <HelpIcon text="Amount a user pays to join. Set to 0 for 'Practice' challenges" />
                  </label>
                  <input
                    type="number"
                    id="entry_fee"
                    name="entry_fee"
                    value={formData.entry_fee}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label
                    htmlFor="platform_stake"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Platform Stake (%){" "}
                    <HelpIcon text="Percentage of total entry fees the platform keeps as revenue (e.g., 15)" />
                  </label>
                  <input
                    type="number"
                    id="platform_stake"
                    name="platform_stake"
                    value={formData.platform_stake}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="15"
                  />
                </div>

                {formData.challenge_type === "Guaranteed" && (
                  <div>
                    <label
                      htmlFor="guaranteed_prize_pool"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Guaranteed Prize Pool ($){" "}
                      <HelpIcon text="Minimum prize promise. This field only appears if 'Guaranteed' is selected" />
                    </label>
                    <input
                      type="number"
                      id="guaranteed_prize_pool"
                      name="guaranteed_prize_pool"
                      value={formData.guaranteed_prize_pool}
                      onChange={handleInputChange}
                      min="0"
                      step="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Minimum prize promise"
                    />
                  </div>
                )}

                <div>
                  <label
                    htmlFor="prize_pool"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Prize Pool ($) <HelpIcon text="Total prize pool amount" />
                  </label>
                  <input
                    type="number"
                    id="prize_pool"
                    name="prize_pool"
                    value={formData.prize_pool}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Allow Overflow Entries - Only for Flexible challenges */}
              {formData.challenge_type === "Flexible" && (
                <div className="mt-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allow_overflow_entries"
                      name="allow_overflow_entries"
                      checked={formData.allow_overflow_entries}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="allow_overflow_entries"
                      className="ml-2 block text-sm font-medium text-gray-700"
                    >
                      Allow Overflow Entries
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    When enabled, participants can join even after
                    max_participants is reached, causing the prize pool to grow
                    dynamically.
                  </p>
                </div>
              )}
            </div>

            {/* Section 4: Prize Distribution Strategy */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 4: Prize Distribution Strategy
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prize Model{" "}
                    <HelpIcon text="A dynamic table where the admin defines the prize for each rank as a percentage (%) of the final prize pool. The tool should ensure the total adds up to 100%." />
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Prize Distribution
                        </h4>
                        <div className="space-y-2">
                          {formData.prize_distribution?.map((item, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-2"
                            >
                              <span className="text-sm text-gray-600 w-8">
                                Rank
                              </span>
                              <input
                                type="number"
                                value={item.rank}
                                onChange={(e) =>
                                  handlePrizeDistributionChange(
                                    index,
                                    "rank",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                min="1"
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <span className="text-sm text-gray-600 w-16">
                                Percentage
                              </span>
                              <input
                                type="number"
                                value={item.percentage}
                                onChange={(e) =>
                                  handlePrizeDistributionChange(
                                    index,
                                    "percentage",
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                min="0"
                                max="100"
                                step="0.1"
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <span className="text-sm text-gray-600">%</span>
                              <button
                                type="button"
                                onClick={() => removePrizeDistribution(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addPrizeDistribution}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            + Add Rank
                          </button>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Summary
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">
                              Total Percentage:
                            </span>
                            <span
                              className={`text-sm font-medium ${
                                getTotalPercentage() === 100
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {getTotalPercentage()}%
                            </span>
                          </div>
                          {getTotalPercentage() !== 100 && (
                            <p className="text-xs text-red-600">
                              Total must equal 100%. Current total:{" "}
                              {getTotalPercentage()}%
                            </p>
                          )}
                          <div className="text-xs text-gray-500">
                            Example: Rank 1: 20%, Rank 2: 10%, Rank 3-5: 5% each
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: App Content (Display Tags & Gameplay Rules) */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 5: App Content (Display Tags & Gameplay Rules)
              </h2>
              {/* Experience Level */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level{" "}
                  <HelpIcon text="Select one experience level only." />
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="experience_level"
                      value="beginner"
                      checked={formData.experience_level === "beginner"}
                      onChange={(e) => handleExperienceLevel(e.target.value)}
                      className="mr-2"
                    />
                    Beginner
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="experience_level"
                      value="intermediate"
                      checked={formData.experience_level === "intermediate"}
                      onChange={(e) => handleExperienceLevel(e.target.value)}
                      className="mr-2"
                    />
                    Intermediate
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="experience_level"
                      value="expert"
                      checked={formData.experience_level === "expert"}
                      onChange={(e) => handleExperienceLevel(e.target.value)}
                      className="mr-2"
                    />
                    Expert
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Risk Appetite{" "}
                  <HelpIcon text="Select one level of risk appetite only." />
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="risk_appetite"
                      value="low"
                      checked={formData.risk_appetite === "low"}
                      onChange={(e) => handleRiskAppetite(e.target.value)}
                      className="mr-2"
                    />
                    Low (I prefer smaller, safer contests)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="risk_appetite"
                      value="medium"
                      checked={formData.risk_appetite === "medium"}
                      onChange={(e) => handleRiskAppetite(e.target.value)}
                      className="mr-2"
                    />
                    Medium
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="risk_appetite"
                      value="high"
                      checked={formData.risk_appetite === "high"}
                      onChange={(e) => handleRiskAppetite(e.target.value)}
                      className="mr-2"
                    />
                    High (I aim for the biggest prize pools)
                  </label>
                </div>
              </div>

              {/* Trading Rules - Allowed Order Types */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed Order Types{" "}
                  <HelpIcon text="Checkboxes for different order types. Market Order is always enabled." />
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.allowed_order_types?.market_order}
                      onChange={(e) =>
                        handleOrderTypeChange("market_order", e.target.checked)
                      }
                      disabled
                      className="mr-2"
                    />
                    Market Order (always enabled)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.allowed_order_types?.limit_orders}
                      onChange={(e) =>
                        handleOrderTypeChange("limit_orders", e.target.checked)
                      }
                      className="mr-2"
                      disabled={formData?.experience_level == "beginner"}
                    />
                    Allow Limit Orders
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.allowed_order_types?.stop_loss_orders}
                      onChange={(e) =>
                        handleOrderTypeChange(
                          "stop_loss_orders",
                          e.target.checked
                        )
                      }
                      className="mr-2"
                      disabled={formData?.experience_level == "beginner"}
                    />
                    Allow Stop-Loss Orders
                  </label>
                </div>
              </div>

              {/* Trading Restrictions */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trading Restrictions{" "}
                  <HelpIcon text="Toggles/Switches for various trading restrictions" />
                </label>

                <div className="space-y-4">
                  {/* Allow Short Selling Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Allow Short Selling
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleTradingRestrictionChange(
                          "allow_short_selling",
                          !formData.trading_restrictions?.allow_short_selling
                        )
                      }
                      className={`${
                        formData.trading_restrictions?.allow_short_selling
                          ? "bg-green-500"
                          : "bg-gray-300"
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                    >
                      <span
                        className={`${
                          formData.trading_restrictions?.allow_short_selling
                            ? "translate-x-6"
                            : "translate-x-1"
                        } inline-block h-4 w-4 transform bg-white rounded-full transition-transform`}
                      />
                    </button>
                  </div>

                  {/* Intraday Only Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Intraday Only (Auto-square off at 3:30 PM)
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleTradingRestrictionChange(
                          "intraday_only",
                          !formData.trading_restrictions?.intraday_only
                        )
                      }
                      className={`${
                        formData.trading_restrictions?.intraday_only
                          ? "bg-green-500"
                          : "bg-gray-300"
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                    >
                      <span
                        className={`${
                          formData.trading_restrictions?.intraday_only
                            ? "translate-x-6"
                            : "translate-x-1"
                        } inline-block h-4 w-4 transform bg-white rounded-full transition-transform`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Position & Risk Management Rules */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="max_open_positions"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Maximum Open Positions{" "}
                    <HelpIcon text="Numerical input (e.g., 10). Leave blank for unlimited." />
                  </label>
                  <input
                    type="number"
                    id="max_open_positions"
                    value={formData.trading_restrictions?.max_open_positions}
                    onChange={(e) =>
                      handleTradingRestrictionChange(
                        "max_open_positions",
                        parseInt(e.target.value) || 0
                      )
                    }
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="10 (leave blank for unlimited)"
                  />
                </div>

                <div>
                  <label
                    htmlFor="max_capital_per_stock"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Maximum % of Capital per Stock{" "}
                    <HelpIcon text="Numerical input (e.g., 25)" />
                  </label>
                  <input
                    type="number"
                    id="max_capital_per_stock"
                    value={formData.trading_restrictions?.max_capital_per_stock}
                    onChange={(e) =>
                      handleTradingRestrictionChange(
                        "max_capital_per_stock",
                        parseInt(e.target.value) || 0
                      )
                    }
                    min="1"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="25"
                  />
                </div>
              </div>

              {/* Gameplay Rules */}
              <div className="mb-6">
                <label
                  htmlFor="rules"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Gameplay Rules{" "}
                  <HelpIcon text="Text editor for detailed gameplay rules" />
                </label>
                <textarea
                  id="rules"
                  name="rules"
                  value={formData.rules}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter detailed gameplay rules..."
                />
              </div>

              {/* Info Box Text */}
              <div className="mb-6">
                <label
                  htmlFor="info_box_text"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Info Box Text{" "}
                  <HelpIcon text="Optional text area for special explanations (e.g., 'This is a Guaranteed plus contest...')" />
                </label>
                <textarea
                  id="info_box_text"
                  name="info_box_text"
                  value={formData.info_box_text}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional special explanations..."
                />
              </div>

              {/* Info Icons Setup */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label
                    htmlFor="first_prize_icon"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    First Prize Icon (🏆){" "}
                    <HelpIcon text="Icon to display for first prize" />
                  </label>
                  <input
                    type="text"
                    id="first_prize_icon"
                    name="first_prize_icon"
                    value={formData.first_prize_icon}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="🏆"
                  />
                </div>

                <div>
                  <label
                    htmlFor="win_percentage_icon"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Win % Icon (📈){" "}
                    <HelpIcon text="Icon to display for win percentage" />
                  </label>
                  <input
                    type="text"
                    id="win_percentage_icon"
                    name="win_percentage_icon"
                    value={formData.win_percentage_icon}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="📈"
                  />
                </div>

                <div>
                  <label
                    htmlFor="max_entries_icon"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Max Entries Icon (Ⓜ️){" "}
                    <HelpIcon text="Icon to display for maximum entries" />
                  </label>
                  <input
                    type="text"
                    id="max_entries_icon"
                    name="max_entries_icon"
                    value={formData.max_entries_icon}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ⓜ️"
                  />
                </div>
              </div>
            </div>

            {/* Section 9: Trading Hours Settings */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 9: Trading Hours Settings
              </h2>

              {/* Master Toggle */}
              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trading_hours_enabled"
                    checked={formData.trading_hours_settings?.enabled || false}
                    onChange={(e) =>
                      handleTradingHoursChange("enabled", e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="trading_hours_enabled"
                    className="ml-2 block text-sm font-medium text-gray-700"
                  >
                    Enable Trading Hours Restrictions
                  </label>
                  <HelpIcon text="Master toggle to enforce specific trading hours for this challenge. If disabled, trading is 24/7 during the Live phase." />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  When enabled, trading will be restricted to specific hours.
                  When disabled, trading is allowed 24/7.
                </p>
              </div>

              {formData.trading_hours_settings?.enabled && (
                <div className="space-y-6">
                  {/* Fixed Window Settings */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h3 className="text-lg font-medium text-blue-900 mb-4">
                      Fixed Trading Window
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="fixed_window_start_time"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Start Time (HH:mm)
                        </label>
                        <input
                          type="time"
                          id="fixed_window_start_time"
                          value={
                            formData.trading_hours_settings
                              ?.fixed_window_start_time || ""
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "fixed_window_start_time",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="09:00"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          e.g., 09:00 for 9:00 AM
                        </p>
                      </div>

                      <div>
                        <label
                          htmlFor="fixed_window_end_time"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          End Time (HH:mm)
                        </label>
                        <input
                          type="time"
                          id="fixed_window_end_time"
                          value={
                            formData.trading_hours_settings
                              ?.fixed_window_end_time || ""
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "fixed_window_end_time",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="15:30"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          e.g., 15:30 for 3:30 PM
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Challenge Type Specific Settings */}
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <h3 className="text-lg font-medium text-green-900 mb-4">
                      Challenge Type Specific Settings
                    </h3>

                    {formData.challenge_type === "Guaranteed" && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="cancel_unexecuted_fixed_limits"
                          checked={
                            formData.trading_hours_settings
                              ?.cancel_unexecuted_fixed_limits || false
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "cancel_unexecuted_fixed_limits",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="cancel_unexecuted_fixed_limits"
                          className="ml-2 block text-sm font-medium text-gray-700"
                        >
                          Cancel Unexecuted Fixed Limits
                        </label>
                        <HelpIcon text="When enabled, any unexecuted limit orders will be cancelled when the trading window closes." />
                      </div>
                    )}

                    {formData.challenge_type === "Flexible" && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="restrict_to_market_hours"
                          checked={
                            formData.trading_hours_settings
                              ?.restrict_to_market_hours || false
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "restrict_to_market_hours",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="restrict_to_market_hours"
                          className="ml-2 block text-sm font-medium text-gray-700"
                        >
                          Restrict to Market Hours (9:15 AM - 3:30 PM)
                        </label>
                        <HelpIcon text="When enabled, trading will be restricted to standard market hours (9:15 AM to 3:30 PM IST)." />
                      </div>
                    )}
                  </div>

                  {/* Information Box */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Trading Hours Information
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <ul className="list-disc list-inside space-y-1">
                            <li>
                              <strong>Fixed Challenges:</strong> Initial
                              portfolio can be defined anytime on Start Date
                            </li>
                            <li>
                              <strong>Dynamic Challenges:</strong> Trading is
                              restricted to specified hours during Live phase
                            </li>
                            <li>
                              <strong>Market Hours:</strong> Standard NSE
                              trading hours are 9:15 AM to 3:30 PM IST
                            </li>
                            <li>
                              <strong>Limit Orders:</strong> Can be placed
                              outside trading hours but executed only during
                              trading hours
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={onBackToChallengeList}
                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin h-4 w-4 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating...
                  </div>
                ) : (
                  "Create Challenge"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
